// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { PowerFlowModelDiagnostics } from '../../../utils/powerFlowModel';
import type { PowerFlowInference } from '../../../utils/powerFlowInference';
import type { PowerFlowLiveState } from '../../../utils/powerFlowLive';
import { formatAge } from '../../../utils/powerFlowCaptions';
import { getEntityDisplayType } from '../../../utils/entityHelpers';

export interface PowerFlowNoticesProps {
  diagnostics: PowerFlowModelDiagnostics;
  /** Structural inference, for the label/loads disagreements and single feeds. */
  inference?: PowerFlowInference;
  /** Live inference, for stale readings and the checks that fail. */
  live?: PowerFlowLiveState;
  /** Epoch ms, for the age of the oldest stale reading. */
  now?: number;
  /** Human name for an asset id. */
  nameOf?: (_id: string) => string;
  /** The twin load stopped before its last page, so the graph is partial. */
  truncated?: boolean;
}

const listNames = (ids: readonly string[], nameOf: (_id: string) => string, max = 5): string =>
  ids.slice(0, max).map(nameOf).join(', ') + (ids.length > max ? ', …' : '');

/**
 * What the twin did not tell us about this site's power chain.
 *
 * **This is part of the diagram, not a debug aid.** A one-line that quietly
 * omits a conductor is worse than no one-line: an operator reading "if this
 * fails, what goes dark?" off an incomplete drawing gets a confidently wrong
 * answer. Every way the model can render thinner than the plant really is gets
 * a line here.
 *
 * Rendered behind a `?` button in the page's bottom-right corner, which opens
 * a popover of plain text lines. On a site with a real gap there can be six of
 * these at once, and a stack of inline notifications would push the diagram
 * off the screen it is the point of; the button's count says how many wait
 * there, and the drawing itself greys stale readings and marks failed checks
 * in place, so nothing here is the only signal.
 */
export const PowerFlowNotices = ({
  diagnostics,
  inference,
  live,
  now,
  nameOf = id => id,
  truncated = false,
}: PowerFlowNoticesProps) => {
  const notices: React.ReactNode[] = [];

  // Before everything else: every other line assumes the payload is whole.
  if (truncated) {
    notices.push(
      <>
        <strong>The twin payload is incomplete.</strong> Paging stopped before the digital twin ran
        out of pages, so assets and connections may be missing from this diagram. Reload to try
        again.
      </>
    );
  }

  // --- What the readings and the inference found ----------------------------
  // These are not data gaps; they are findings. They go first because an
  // operator acts on them before caring how the payload was encoded.
  if (live) {
    const failing = Array.from(live.redundancyOf.values()).filter(check => !check.holds);
    if (failing.length > 0) {
      const worst = failing.reduce((max, check) => (check.ratio > max.ratio ? check : max));
      notices.push(
        <>
          <strong>N+1 capacity is marginal</strong> on {failing.length}{' '}
          {failing.length === 1 ? 'board' : 'boards'}: the load exceeds the UPS rating that would
          remain after losing the largest unit feeding it (
          {listNames(
            failing.map(check => check.boardId),
            nameOf
          )}
          ; worst {Math.round(worst.ratio * 100)} % of the spare capacity). A capacity screen of
          measured load against the published ratings, total minus the largest — not a proof of
          availability. Confirm the ratings&apos; overload margin before acting.
        </>
      );
    }

    const bothLive = Array.from(live.selectedSourceOf.entries()).filter(
      ([, selected]) => selected.length > 1
    );
    if (bothLive.length > 0) {
      notices.push(
        <>
          <strong>{bothLive.length}</strong> {bothLive.length === 1 ? 'board has' : 'boards have'}{' '}
          more than one source carrying load at once (
          {listNames(
            bothLive.map(([id]) => id),
            nameOf
          )}
          ). A transfer in progress, or a parallel test — the drawing shows both and picks neither.
        </>
      );
    }

    if (live.staleReadingCount > 0) {
      const age =
        live.oldestStaleAt !== undefined && now !== undefined
          ? formatAge(now - live.oldestStaleAt)
          : undefined;
      notices.push(
        <>
          <strong>{live.staleReadingCount.toLocaleString()}</strong>{' '}
          {live.staleReadingCount === 1 ? 'reading is' : 'readings are'} stale
          {age ? ` (oldest ${age} old)` : ''} and drive no inference here; they are shown greyed on
          the asset. {live.freshReadingCount.toLocaleString()} are current.
        </>
      );
    }
  }

  if (inference) {
    const disagreements = Array.from(inference.branchOf.entries()).filter(
      ([, cls]) => !cls.labelAgrees
    );
    if (disagreements.length > 0) {
      notices.push(
        <>
          <strong>{disagreements.length}</strong>{' '}
          {disagreements.length === 1 ? 'board is' : 'boards are'} labelled one way and wired
          another (
          {listNames(
            disagreements.map(([id]) => id),
            nameOf
          )}
          ). The branch is drawn from what it feeds; the label is worth checking in the site&apos;s
          asset records.
        </>
      );
    }
  }

  if (diagnostics.clean && notices.length === 0) return null;

  if (diagnostics.unconnectedCount > 0) {
    const breakdown = diagnostics.unconnected
      .slice(0, 4)
      .map(row => `${row.count} × ${getEntityDisplayType(row.entityType)}`)
      .join(', ');
    notices.push(
      <>
        <strong>{diagnostics.unconnectedCount}</strong>{' '}
        {diagnostics.unconnectedCount === 1 ? 'asset has' : 'assets have'} no power connections
        published, so {diagnostics.unconnectedCount === 1 ? 'it is' : 'they are'} not on the diagram
        ({breakdown}
        {diagnostics.unconnected.length > 4 ? ', …' : ''}).
      </>
    );
  }

  if (diagnostics.unknownRelationshipTypes.length > 0) {
    // The one that matters most. A namespace change upstream
    // (`brick:feeds` rather than `feeds`) makes every conductor vanish, and
    // without this line the diagram would simply look sparse.
    notices.push(
      <>
        Unrecognised relationship{' '}
        {diagnostics.unknownRelationshipTypes.length === 1 ? 'type' : 'types'} in this site&apos;s
        twin:{' '}
        {diagnostics.unknownRelationshipTypes.map((type, index) => (
          <React.Fragment key={type}>
            {index > 0 && ', '}
            <code>{type}</code>
          </React.Fragment>
        ))}
        . Power-flow direction is matched on the unprefixed predicate name, so connections using
        these are not drawn.
      </>
    );
  }

  if (diagnostics.attributeTopologyCount > 0) {
    notices.push(
      <>
        <strong>{diagnostics.attributeTopologyCount}</strong>{' '}
        {diagnostics.attributeTopologyCount === 1 ? 'asset carries' : 'assets carry'} power topology
        as an attribute rather than as a relationship. The diagram is built from relationships, so
        those connections are not drawn.
      </>
    );
  }

  if (diagnostics.fluidPathCount > 0) {
    notices.push(
      <>
        <strong>{diagnostics.fluidPathCount}</strong>{' '}
        {diagnostics.fluidPathCount === 1 ? 'element' : 'elements'} of the air and chilled-water
        paths {diagnostics.fluidPathCount === 1 ? 'is' : 'are'} not drawn (
        {diagnostics.fluidPathTypes.map((type, index) => (
          <React.Fragment key={type}>
            {index > 0 && ', '}
            <code>{type}</code>
          </React.Fragment>
        ))}
        ). The twin connects them with <code>feeds</code>, which is the cooling loop rather than a
        conductor, so they belong to Floor Plan. The pumps, fans and chillers on that loop are still
        drawn, as loads on the switchboard that powers them.
      </>
    );
  }

  if (diagnostics.uncontainedTerminalLinkCount > 0) {
    notices.push(
      <>
        <strong>{diagnostics.uncontainedTerminalLinkCount}</strong>{' '}
        {diagnostics.uncontainedTerminalLinkCount === 1 ? 'connection runs' : 'connections run'}{' '}
        through a breaker no switchboard lists as its part (<code>hasPart</code>), so{' '}
        {diagnostics.uncontainedTerminalLinkCount === 1 ? 'it is' : 'they are'} not drawn.
      </>
    );
  }

  if (diagnostics.danglingLinkCount > 0) {
    notices.push(
      <>
        <strong>{diagnostics.danglingLinkCount}</strong>{' '}
        {diagnostics.danglingLinkCount === 1 ? 'connection' : 'connections'} name an asset that is
        not in this site&apos;s twin payload.
      </>
    );
  }

  if (diagnostics.suspectRoots.length > 0) {
    notices.push(
      <>
        <strong>{diagnostics.suspectRoots.length}</strong>{' '}
        {diagnostics.suspectRoots.length === 1 ? 'asset appears' : 'assets appear'} at the top of
        the diagram without being a power source, so{' '}
        {diagnostics.suspectRoots.length === 1
          ? 'the connection feeding it is'
          : 'the connections feeding them are'}{' '}
        missing: {listNames(diagnostics.suspectRoots, nameOf)}.
      </>
    );
  }

  if (diagnostics.roleViolations.length > 0) {
    notices.push(
      <>
        <strong>{diagnostics.roleViolations.length}</strong>{' '}
        {diagnostics.roleViolations.length === 1 ? 'asset has' : 'assets have'} connections the
        ontology does not allow for its equipment type:{' '}
        {listNames(diagnostics.roleViolations, nameOf)}.
      </>
    );
  }

  if (diagnostics.unmappedTypes.length > 0) {
    notices.push(
      <>
        Drawn without one-line notation because{' '}
        {diagnostics.unmappedTypes.length === 1
          ? 'this equipment type is'
          : 'these equipment types are'}{' '}
        not yet mapped: {diagnostics.unmappedTypes.map(getEntityDisplayType).join(', ')}.
      </>
    );
  }

  return <NoticesPopover notices={notices} />;
};

interface NoticesPopoverProps {
  notices: React.ReactNode[];
}

/** The `?` button and the panel it toggles. Renders nothing with no notices. */
const NoticesPopover = ({ notices }: NoticesPopoverProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const toggle = useCallback(() => setOpen(value => !value), []);

  // Escape and a click anywhere else close it, as a tooltip would.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  if (notices.length === 0) return null;
  const label = `${notices.length} ${notices.length === 1 ? 'notice' : 'notices'} about this diagram`;

  return (
    <div className="sld-notices-host" ref={rootRef}>
      {open && (
        <div className="sld-notices-panel" id={panelId} role="region" aria-label={label}>
          <ul className="sld-notices" data-testid="power-flow-notices">
            {notices.map((notice, index) => (
              // Index keys: the list is derived wholesale from one diagnostics object
              // and never reordered or partially updated.
              // eslint-disable-next-line react/no-array-index-key
              <li key={index}>{notice}</li>
            ))}
          </ul>
        </div>
      )}
      <button
        type="button"
        className="sld-notices-toggle"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        title={label}
        data-testid="power-flow-notices-toggle"
      >
        <span aria-hidden="true">?</span>
        <span className="sld-notices-toggle__count" aria-hidden="true">
          {notices.length}
        </span>
      </button>
    </div>
  );
};
